 <template>
  <div class="help-root">

    <div class="help-intro">
      <span class="material-symbols-outlined help-hero-icon">swap_horiz</span>
      <p>
        Gestiona las referencias y derivaciones de una atención específica:
        registra el destino, especialidad, motivo clínico y genera el
        formulario oficial en PDF.
      </p>
    </div>

    <!-- Datos de la atención -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">article</span>
        Datos de la atención (solo lectura)
      </h3>
      <div class="help-cards">
        <div class="help-card" v-for="item in datosAtencion" :key="item.titulo">
          <span class="material-symbols-outlined help-card-icon">{{ item.icon }}</span>
          <div>
            <p class="help-card-title">{{ item.titulo }}</p>
            <p class="help-card-desc">{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Campos del formulario -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">edit_note</span>
        Campos del formulario
      </h3>
      <div class="help-cards">
        <div class="help-card" v-for="item in campos" :key="item.titulo">
          <span class="material-symbols-outlined help-card-icon">{{ item.icon }}</span>
          <div>
            <p class="help-card-title">{{ item.titulo }}</p>
            <p class="help-card-desc">{{ item.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Dictado por voz -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">mic</span>
        Dictado por voz
      </h3>
      <div class="help-voz">
        <div class="help-voz-item">
          <span class="material-symbols-outlined" style="color:orangered;font-size:28px">mic_off</span>
          <p class="help-card-desc">Micrófono <strong>inactivo</strong>. Pulsa para iniciar el dictado en el campo correspondiente.</p>
        </div>
        <div class="help-voz-item">
          <span class="material-symbols-outlined" style="color:green;font-size:28px">mic</span>
          <p class="help-card-desc">Micrófono <strong>activo</strong>. El sistema transcribe tu voz. Pulsa para detener.</p>
        </div>
      </div>
      <p class="help-card-desc" style="padding:0 0.25rem;margin-top:0.4rem">
        Disponible en los campos: <strong>Motivo</strong>, <strong>Resumen</strong>,
        <strong>Hallazgos</strong> y <strong>Tratamiento</strong>.
      </p>
    </section>

    <!-- Acciones de la tabla -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">touch_app</span>
        Acciones disponibles
      </h3>
      <div class="help-actions">
        <div class="help-action" v-for="a in acciones" :key="a.titulo">
          <span class="material-symbols-outlined help-action-icon" :style="{ color: a.color }">{{ a.icon }}</span>
          <div>
            <p class="help-card-title">{{ a.titulo }}</p>
            <p class="help-card-desc">{{ a.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Restricción por estado -->
    <section class="help-section">
      <h3 class="help-section-title">
        <span class="material-symbols-outlined">lock</span>
        Restricciones por estado de la atención
      </h3>
      <div class="help-estados" style="margin-bottom:0.5rem">
        <span class="help-estado-badge" style="background:#fff3cd;color:#856404">PENDIENTE</span>
        <span class="help-estado-badge" style="background:#cce5ff;color:#004085">PROCESO</span>
      </div>
      <p class="help-card-desc" style="padding:0 0.25rem">
        Crear, editar, actualizar y eliminar referencias solo está habilitado
        cuando la atención está en estado <strong>PENDIENTE</strong> o
        <strong>PROCESO</strong>. En otros estados solo se pueden ver los
        detalles y generar el PDF.
      </p>
    </section>

    <div class="help-tip">
      <span class="material-symbols-outlined">lightbulb</span>
      <p>
        El campo <strong>Justificada</strong> es obligatorio e indica si la
        referencia o derivación está clínicamente justificada. Asegúrate de
        completar el <strong>Motivo</strong> antes de guardar.
      </p>
    </div>

  </div>
</template>

<script>
export default {
  name: "AyudaAtencionReferencia",
  data() {
    return {
      datosAtencion: [
        {
          icon: "person",
          titulo: "Paciente y cédula",
          desc:   "Nombre completo y número de documento del paciente de la atención. No es editable desde esta pantalla.",
        },
        {
          icon: "stethoscope",
          titulo: "Médico y especialidad",
          desc:   "Médico tratante y especialidad de la atención de origen.",
        },
        {
          icon: "text_snippet",
          titulo: "Motivo y observación",
          desc:   "Descripción del motivo de consulta y observaciones de la atención principal.",
        },
        {
          icon: "expand_more",
          titulo: "Más información",
          desc:   "Despliega el ID de la atención y las fechas de registro y última actualización.",
        },
      ],
      campos: [
        {
          icon: "label",
          titulo: "Motivo Ref./Der.",
          desc:   "Selecciona el motivo clínico por el cual se emite la referencia o derivación.",
        },
        {
          icon: "business",
          titulo: "Destino",
          desc:   "Unidad de salud o institución a la que se envía al paciente.",
        },
        {
          icon: "local_hospital",
          titulo: "Especialidad y médico",
          desc:   "Especialidad y médico de destino que atenderá al paciente referido.",
        },
        {
          icon: "calendar_today",
          titulo: "Fecha y hora",
          desc:   "Fecha y hora programada para la atención en la unidad de destino.",
        },
        {
          icon: "category",
          titulo: "Tipo",
          desc:   "R: Referencia (envío a otra unidad de salud) — D: Derivación (envío a otra especialidad dentro de la misma unidad).",
        },
        {
          icon: "text_snippet",
          titulo: "Motivo, Resumen, Hallazgos y Tratamiento",
          desc:   "Campos clínicos con soporte de dictado por voz. Registran el detalle de la condición del paciente y el tratamiento previo recibido.",
        },
        {
          icon: "verified",
          titulo: "Justificada",
          desc:   "Indica si la referencia o derivación está clínicamente justificada (Sí / No). Campo obligatorio.",
        },
      ],
      acciones: [
        {
          icon:   "add_circle",
          color:  "#0059cc",
          titulo: "Nueva referencia",
          desc:   "Abre el formulario para registrar una nueva referencia o derivación de la atención. Solo disponible en atenciones PENDIENTE o PROCESO.",
        },
        {
          icon:   "edit",
          color:  "#198754",
          titulo: "Editar",
          desc:   "Abre el formulario modal para modificar los datos de la referencia. Los campos quedan deshabilitados si la atención no está en PENDIENTE o PROCESO.",
        },
        {
          icon:   "edit_note",
          color:  "#6f42c1",
          titulo: "Ver detalles",
          desc:   "Navega al formulario completo de la referencia para revisar o completar todos los datos clínicos registrados.",
        },
        {
          icon:   "picture_as_pdf",
          color:  "#dc3545",
          titulo: "Formulario PDF",
          desc:   "Genera y descarga en PDF el formulario oficial de referencia/derivación para su impresión o archivo.",
        },
        {
          icon:   "delete",
          color:  "#dc3545",
          titulo: "Eliminar",
          desc:   "Elimina la referencia de forma permanente previa confirmación. Solo disponible en atenciones PENDIENTE o PROCESO.",
        },
      ],
    };
  },
};
</script>

<style src="@/assets/css/ayuda.css" scoped></style>